<script setup lang="ts">
import { AlertTriangle, CheckCircle2, X } from "lucide-vue-next";

const props = defineProps<{
  modelValue: boolean;
  title: string;
  description: string;
  confirmText: string;
  cancelText: string;
  variant?: "danger" | "primary";
  loading?: boolean;
}>();

const emit = defineEmits<{
  "update:modelValue": [value: boolean];
  confirm: [];
}>();

let previousHtmlOverscrollBehavior = "";
let previousBodyOverscrollBehavior = "";

const isDanger = computed(() => props.variant === "danger");

function preventScroll(event: Event) {
  event.preventDefault();
}

function preventScrollKeys(event: KeyboardEvent) {
  const blockedKeys = [
    "ArrowUp",
    "ArrowDown",
    "PageUp",
    "PageDown",
    "Home",
    "End",
    " ",
  ];

  if (blockedKeys.includes(event.key)) {
    event.preventDefault();
  }
}

function lockBodyScroll() {
  if (typeof document === "undefined") return;

  const html = document.documentElement;
  const body = document.body;

  previousHtmlOverscrollBehavior = html.style.overscrollBehavior;
  previousBodyOverscrollBehavior = body.style.overscrollBehavior;

  html.style.overscrollBehavior = "none";
  body.style.overscrollBehavior = "none";

  html.classList.add("app-dialog-scroll-lock");
  body.classList.add("app-dialog-scroll-lock");

  window.addEventListener("wheel", preventScroll, { passive: false });
  window.addEventListener("touchmove", preventScroll, { passive: false });
  window.addEventListener("keydown", preventScrollKeys, { passive: false });
}

function unlockBodyScroll() {
  if (typeof document === "undefined") return;

  const html = document.documentElement;
  const body = document.body;

  html.style.overscrollBehavior = previousHtmlOverscrollBehavior;
  body.style.overscrollBehavior = previousBodyOverscrollBehavior;

  html.classList.remove("app-dialog-scroll-lock");
  body.classList.remove("app-dialog-scroll-lock");

  window.removeEventListener("wheel", preventScroll);
  window.removeEventListener("touchmove", preventScroll);
  window.removeEventListener("keydown", preventScrollKeys);
}

function close() {
  if (props.loading) return;

  emit("update:modelValue", false);
}

function confirm() {
  if (props.loading) return;

  emit("confirm");
}

function handleEscPress(event: KeyboardEvent) {
  if (event.key === "Escape") {
    close();
  }
}

watch(
  () => props.modelValue,
  value => {
    if (value) {
      lockBodyScroll();
    } else {
      unlockBodyScroll();
    }
  },
);

onMounted(() => {
  window.addEventListener("keyup", handleEscPress);

  if (props.modelValue) {
    lockBodyScroll();
  }
});

onBeforeUnmount(() => {
  window.removeEventListener("keyup", handleEscPress);
  unlockBodyScroll();
});
</script>

<template>
  <Teleport to="body">
    <Transition name="dialog-backdrop">
      <button
        v-if="modelValue"
        type="button"
        class="fixed inset-0 z-[100] bg-black/50"
        aria-label="Close dialog"
        @click="close"
      />
    </Transition>

    <Transition name="dialog-panel">
      <div
        v-if="modelValue"
        class="fixed inset-x-4 top-1/2 z-[101] mx-auto flex max-w-[420px] -translate-y-1/2 flex-col rounded-2xl border border-app-border bg-white p-5 text-center shadow-[0_24px_80px_rgba(15,23,42,0.22)]"
      >
        <button
          type="button"
          class="absolute right-4 top-4 flex size-8 items-center justify-center rounded-md text-app-muted transition hover:bg-gray-100 hover:text-app-text"
          @click="close"
        >
          <X class="size-5" />
        </button>

        <div
          class="mx-auto flex size-14 items-center justify-center rounded-full"
          :class="
            isDanger
              ? 'bg-red-50 text-app-danger'
              : 'bg-primary-100 text-primary'
          "
        >
          <AlertTriangle v-if="isDanger" class="size-7" />
          <CheckCircle2 v-else class="size-7" />
        </div>

        <h2 class="mt-4 text-xl font-semibold text-app-text">
          {{ title }}
        </h2>

        <p class="mt-2 text-base text-app-muted">
          {{ description }}
        </p>

        <div v-if="$slots.default" class="mt-5 text-left">
          <slot />
        </div>

        <div class="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <button
            type="button"
            class="flex w-full select-none items-center justify-center rounded-lg border border-app-border bg-white px-4 py-2.5 text-sm font-semibold text-app-text transition hover:bg-gray-100"
            @click="close"
          >
            {{ cancelText }}
          </button>

          <button
            type="button"
            :disabled="loading"
            class="flex w-full select-none items-center justify-center rounded-lg px-4 py-2.5 text-sm font-semibold text-white transition disabled:cursor-not-allowed"
            :class="
              isDanger
                ? 'bg-app-danger hover:bg-red-400 disabled:bg-red-400'
                : 'bg-primary hover:bg-primary-400 disabled:bg-primary-400'
            "
            @click="confirm"
          >
            {{ confirmText }}
          </button>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.dialog-backdrop-enter-active,
.dialog-backdrop-leave-active {
  transition: opacity 160ms ease;
}

.dialog-backdrop-enter-from,
.dialog-backdrop-leave-to {
  opacity: 0;
}

.dialog-panel-enter-active,
.dialog-panel-leave-active {
  transition:
    opacity 180ms ease,
    transform 180ms ease;
}

.dialog-panel-enter-from,
.dialog-panel-leave-to {
  opacity: 0;
  transform: translateY(-46%) scale(0.98);
}

:global(html.app-dialog-scroll-lock) {
  scrollbar-color: rgba(15, 23, 42, 0.45) rgba(15, 23, 42, 0.18);
}

:global(html.app-dialog-scroll-lock::-webkit-scrollbar) {
  width: 12px;
}

:global(html.app-dialog-scroll-lock::-webkit-scrollbar-track) {
  background: rgba(15, 23, 42, 0.18);
}

:global(html.app-dialog-scroll-lock::-webkit-scrollbar-thumb) {
  border: 3px solid rgba(15, 23, 42, 0.18);
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.45);
}
</style>
